import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
    getIdentityProviderDetailsReferenceDataReduxRequest,
    getIdentityProviderDetailsReduxRequest
} from "@redux-requests/identity-provider-details";
import { LoadingIndicator } from "../common/loading-indicator/loading-indicator";
import { IdentityProviderDetailsComponent } from "./identity-provider-details-component";
import { resources } from "@constants/hardcoded-strings";
import { CardContainer } from "../common/card-container/card-container";
import { ContentPageHeader } from "@containers/common/content-page-header/content-page-header";
import { ContentPageBody } from "@containers/common/content-page-body/content-page-body";
import { IdentityProviderChangeRequestResponse } from "@idw-types/data/identity-providers";
import { ChangeRequestStatusTypes } from "@enums/change-request-status-types";
import { IDENTITY_PROVIDERS_URL } from "@constants/routes";
import { navigateToUtil } from "@utils/navigation-util";
import { setIdentityProviderDetailsReferenceData, setIdentityProviderDetailsReferenceDataLoading } from "@redux/actions/identity-provider-details-actions";
import { getIdentityProviderDetailsDataState, getIdentityProviderDetailsReferenceDataState } from "@redux/selectors/identity-provider-details-selectors";

export const IdentityProviderDetailsContainer = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const routerLocation = useLocation();

    const { identityProviderId } = useParams<{ identityProviderId: string }>();

    const identityProviderReferenceData = useSelector(getIdentityProviderDetailsReferenceDataState);
    const {
        isLoadingIdentityProviderDetails,
        identityProviderDetails
    } = useSelector(getIdentityProviderDetailsDataState);
    const [shouldLoadDetailsReferenceData, setShouldLoadDetailsReferenceData] = useState(true);
    const [shouldLoadIdentityProviderDetails, setShouldLoadIdentityProviderDetails] = useState(true);
    const [identityProviderChangeRequests, setIdentityProviderChangeRequests] = useState<Array<IdentityProviderChangeRequestResponse>>([]);

    useEffect(() => {
        if (!identityProviderReferenceData.isLoading && shouldLoadDetailsReferenceData) {
            setShouldLoadDetailsReferenceData(false);
            getIdentityProviderDetailsReferenceDataReduxRequest(dispatch, setIdentityProviderDetailsReferenceDataLoading, setIdentityProviderDetailsReferenceData)();
        }
    }, []);

    const isDetailsReferenceDataPresent = useMemo(() => {
        return !!(
            identityProviderReferenceData.attributeTypeFields &&
            identityProviderReferenceData.attributeTypes &&
            identityProviderReferenceData.categories &&
            identityProviderReferenceData.classifications &&
            identityProviderReferenceData.companies &&
            identityProviderReferenceData.defaultScheme &&
            identityProviderReferenceData.loas &&
            identityProviderReferenceData.protocols &&
            identityProviderReferenceData.schemes &&
            identityProviderReferenceData.urlTypes
        );
    }, [identityProviderReferenceData]);

    const redirectToIdentityProvidersPage = () => {
        navigateToUtil(IDENTITY_PROVIDERS_URL, navigate, routerLocation.pathname);
    };

    const loadIdentityProviderDetailsAndChangeRequests = async (showError: boolean) => {
        const { identityProviderChangeRequests } = await getIdentityProviderDetailsReduxRequest(dispatch)(
            identityProviderId,
            identityProviderReferenceData.categories,
            identityProviderReferenceData.classifications,
            identityProviderReferenceData.companies,
            identityProviderReferenceData.loas,
            identityProviderReferenceData.protocols,
            identityProviderReferenceData.schemes,
            redirectToIdentityProvidersPage,
            showError
        );
        setIdentityProviderChangeRequests(identityProviderChangeRequests);
    };

    useEffect(() => {
        if (isDetailsReferenceDataPresent && !isLoadingIdentityProviderDetails && shouldLoadIdentityProviderDetails) {
            setShouldLoadIdentityProviderDetails(false);
            loadIdentityProviderDetailsAndChangeRequests(true);
        }
    }, [isDetailsReferenceDataPresent, isLoadingIdentityProviderDetails, shouldLoadIdentityProviderDetails]);

    const isLoading = useMemo(() => {
        return isLoadingIdentityProviderDetails || identityProviderReferenceData.isLoading;
    }, [identityProviderReferenceData.isLoading, isLoadingIdentityProviderDetails]);

    const isLoaded = useMemo(() => {
        return !!(!isLoading &&
            identityProviderDetails &&
            isDetailsReferenceDataPresent &&
            !shouldLoadDetailsReferenceData &&
            !shouldLoadIdentityProviderDetails)
    }, [
        isLoading,
        identityProviderDetails,
        isDetailsReferenceDataPresent,
        shouldLoadDetailsReferenceData,
        shouldLoadIdentityProviderDetails
    ]);

    const handleUpdateChangeRequest = async (changeRequest: IdentityProviderChangeRequestResponse) => {
        if (changeRequest.changeRequestStatus === ChangeRequestStatusTypes.CANCELED) {
            loadIdentityProviderDetailsAndChangeRequests(false)
        } else if (changeRequest.changeRequestStatus === ChangeRequestStatusTypes.DEPLOYED) {
            setIdentityProviderChangeRequests([]);
        } else {
            setIdentityProviderChangeRequests([changeRequest]);
        }
    };

    return (
        <CardContainer>
            <ContentPageHeader title={resources.pages.identityProviderDetails.header} />
            <ContentPageBody isMaxWidth={true}>
                {isLoaded && (
                    <IdentityProviderDetailsComponent
                        identityProviderDetails={identityProviderDetails}
                        identityProviderChangeRequests={identityProviderChangeRequests}
                        identityProviderReferenceData={identityProviderReferenceData}
                        setIdentityProviderChangeRequest={handleUpdateChangeRequest}
                    />
                )}
                {isLoading && (
                    <LoadingIndicator title={resources.common.loadingIndicatorText} />
                )}
            </ContentPageBody>
        </CardContainer>
    );
};